// React and core libraries
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';

// State management
import { Provider, useSelector, useDispatch } from 'react-redux';
import { RootState, store } from '@Redux/detail/store';
import {
    setIsAnchorShow,
    setCallJson,
    setIsSearchPanelFixed,
    setFindNearGroup,
    setGroupDetailSync,
    setStationInfo,
    setPricesInfo,
    setPricesInfoSUCCESS,
    setFlightInfo,
} from '@Redux/detail/Group/actions';
import { setItineraries, setOptionalInfo, setNoticeinfo } from '@Redux/detail/Itineraries/actions';
import { I_Group } from '@Redux/detail/Group/interfaces';
import { setPageType } from '@Redux/common/Page/actions';

// Third-party libraries
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

// Day.js and its plugins
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

// Component styles
import 'normalize.css';
import '@magaele/cd_gpsm';
import '@magaele/cd_gpsm/css.css';
import '@magaele/ic-ln/css.css';
import '@magaele/tb_lnrw/css.css';
import './styles.scss';

// Components
import SearchPanelBlock from '@components/Common/SearchPanelBlock';
import SlidePage from '@components/Common/SlidePage';
import { Magnifier, GuideWarning } from '@components/Common/Icons';
import StickyHeader from '@components/DetailPage/StickyHeader';

// Utils and helpers
import { changeDetailHistory } from '@utils/utils';
import DomainByEnv from '@utils/domainByEnv';
import { SideBar } from '@utils/SideBar';
import { createSearchPanel } from '@utils/createSearchPanel';
import i18n from '@utils/i18n';

// Assets
import abandonedImg from './imgs/close.png';
import groupThemeImg from './imgs/group.jpg';

const GroupIntro = React.lazy(() => import('@components/DetailPage/GroupIntro'));
const PrintLightbox = React.lazy(() => import('@components/DetailPage/PrintLightbox'));
const Itinerary = React.lazy(() => import('@components/DetailPage/Itinerary'));
const Recommends = React.lazy(() => import('@components/DetailPage/Recommends'));
const LightBox = React.lazy(() =>
    import('@lion-design/core').then((m) => ({ default: m.LightBox }))
);

dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

declare global {
    interface Window {
        dataLayer: any;
    }
}

interface I_Banner {
    Data: Array<{
        ImageURL: string;
        ProductURL: string;
        ProductID: string;
        ProductName: string;
        TradePrice: number;
        DirectPrice: number;
        ProductDiscount: number;
        ProductLevel: number;
        ProductCountry: any;
        ProductCity: any;
        ProductAllowDate: any;
        ProductCurr: string;
        ProductType: string;
    }>;
    Message: any;
}

interface I_searchBlockM {
    isMobile: boolean;
}

i18n();

window.dataLayer = window.dataLayer || []; // ga4
let ga4GroupID: string = '';

let fixedTopElement: HTMLElement | null | undefined;

const SearchPanelBlockM = ({ isMobile }: I_searchBlockM) => {
    const [isOpenPanel, setIsOpenPanel] = useState<boolean>(false);
    const { isSearchPanelFixed, isAnchorShow } = useSelector(
        (state: RootState) => state.switcherReducer
    );
    const dispatch = useDispatch();
    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;
    const { GroupInfo }: I_Group = JSON.parse(travelinfo!);

    // 控制滑動時錨點、搜尋引擎等物件顯示
    useScrollPosition(({ prevPos, currPos }) => {
        if (!GroupInfo?.GroupID) return;
        const stickyHeight = isMobile ? 90 : 200;

        const isFeatureIntoScreen =
            -currPos.y + stickyHeight >
            (document.querySelector('.itinerary_wrapper') as HTMLDivElement).offsetTop;

        if (isAnchorShow !== isFeatureIntoScreen) {
            dispatch(setIsAnchorShow(isFeatureIntoScreen));
        }

        const isScrollPage = -currPos.y > 150;

        if (isSearchPanelFixed !== isScrollPage) {
            dispatch(setIsSearchPanelFixed(isScrollPage));
        }
    });

    useEffect(() => {
        createSearchPanel({
            device: 'M',
            mCloseBtn: setIsOpenPanel,
            travelType: GroupInfo.TravelType.toString(),
        });

        fixedTopElement = document.getElementById('fixedTopElement'); // 因應 deeplink M 版畫面供站長抓取的 id

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const moduleName = 'searchPanel_block_m';

    return (
        <div className={cN('fixedTopElementDom')}>
            <div
                id="fixedTopElement"
                className={cN(
                    moduleName,
                    isSearchPanelFixed ? 'fixed' : '',
                    isAnchorShow ? 'showAnchor' : '',
                    isOpenPanel ? 'openSearch' : ''
                )}
            >
                <span
                    className={cN(`${moduleName}_input`, isAnchorShow ? 'hideSearch' : '')}
                    onClick={() => setIsOpenPanel(true)}
                >
                    <Magnifier fill="#f03742" />
                    <span>搜尋</span>
                </span>
                <SlidePage
                    visible={isOpenPanel}
                    setIsVisible={setIsOpenPanel}
                    title="團體旅遊"
                    isHeaderShow={false}
                >
                    <div id="SearchPanelM" />
                </SlidePage>
                <StickyHeader />
            </div>
        </div>
    );
};

declare global {
    interface Window {
        SearchPanel: any;
        Templates: {
            login_lion: {
                default: {
                    OpenLogin(): any;
                };
            };
        };
    }
}

enum themeTypes {
    bike = '9',
    cruise = '11',
    festival = '46',
    railway = '48',
    cuisine = '60',
    art = '61',
    bio = '62',
    sportBike = '63',
    music = '64',
    photography = '65',
    culture = '66',
    architecture = '67',
    creativity = '68',
    ruins = '70',
    camping = '71',
    parentChild = '73',
    adventure = '77',
    marathon = '76',
    farm = '81',
}

const Detail = () => {
    const CommonRoute = `${DomainByEnv()}/common`;
    const { travelinfo } = document.getElementById('detailPagePara')!.dataset!;
    const { GroupInfo, TelType, IsPreview }: I_Group = JSON.parse(travelinfo!);
    const dispatch = useDispatch();
    const [isPrintOpen, setisPrintOpen] = useState<boolean>(false);
    const { groupReducer, callInfoReducer, calendarInfoReducer } = useSelector(
        (store: RootState) => store
    );
    const { CalendarGroups } = calendarInfoReducer;
    const { Schedules } = groupReducer;
    const [isTimer, setIsTimer] = useState(false);
    const [openLightbox, setOpenLightBox] = useState(false);
    const [banner, setBanner] = useState<I_Banner>({
        Data: [],
        Message: '',
    });
    const [swap, setSwap] = useState(false);
    const [recommends, setRecommends] = useState(false);
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const [mouseLeave, setMouseLeave] = useState(false);
    const { IsEnsureGroup, Status } = groupReducer.GroupInfo;
    const [nullGroup, setNullGroup] = useState(false);
    // 顯示挪團/取消光箱
    const [isCancelled, setIsCancelled] = useState(false);
    const { isLoadingCalendar } = CalendarGroups;
    // 有無其他出發日期
    const hasMoreGroups =
        Schedules?.TourIDList && Schedules?.TourIDList[0]?.TourSummaryList?.length > 0;
    const arriveID = GroupInfo.ArriveID;
    const checkNewItinerary =
        arriveID && arriveID.length > 0
            ? `/?search?DepartureID=&ArriveID=${arriveID}`
            : '//www.liontravel.com';

    const togglePrintLbx = useCallback((isOpen: boolean) => {
        setisPrintOpen(isOpen);
    }, []);

    // 從後端提供的 div 提供的資料預先載入
    useEffect(() => {
        //現在頁面
        dispatch(setPageType('detail'));
        SideBar(togglePrintLbx);
        const callJson = {
            TelType,
            TourSource: GroupInfo.TourSource,
        };
        dispatch(setCallJson(callJson)); // 請洽專員資料

        dispatch(setGroupDetailSync({ IsPreview, GroupInfo, TelType })); // 預覽模式與初始團資料

        if (IsPreview) {
            const reqBody = {
                GroupID: GroupInfo.GroupID,
                TourSource: GroupInfo.TourSource,
            };
            const FlightInfoReqBody = {
                GroupID: GroupInfo.GroupID,
                NormGroupID: GroupInfo.NormGroupID,
                GoDate: dayjs(GroupInfo.GoDate).format('YYYY-MM-DD'),
                TourSource: GroupInfo.TourSource,
            };
            const itinerariesReqbody = {
                TourID: GroupInfo.TourID,
                IsPreview: IsPreview,
                TravelType: GroupInfo.TravelType,
                TourSource: GroupInfo.TourSource,
            };

            dispatch(setItineraries(itinerariesReqbody));
            dispatch(setStationInfo(reqBody));
            dispatch(setOptionalInfo(reqBody));
            dispatch(setNoticeinfo(reqBody));
            dispatch(setPricesInfoSUCCESS(null));
            dispatch(setPricesInfo(reqBody));
            dispatch(setFlightInfo(FlightInfoReqBody));
        }

        // 就算刪除localStorage也不重新抓資料
        let timer: ReturnType<typeof setInterval> | null = null;

        function handleMouseMove() {
            let open = false;
            if (timer) {
                clearInterval(timer);
            }
            setIsTimer(false);
            if (!open) {
                timer = setInterval(() => {
                    open = true;
                    setIsTimer(true);
                }, 300000);
            }
        }

        function handleMouseLeave() {
            const date = new Date();
            const today = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
            const Recommend = localStorage.getItem('Recommend');
            if (!Recommend || today !== Recommend) {
                setMouseLeave(true);
            } else {
                setMouseLeave(false);
            }
        }
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseleave', handleMouseLeave);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [travelinfo, TelType, dispatch, togglePrintLbx, IsPreview]);

    useEffect(() => {
        const date = new Date();
        const today = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
        const Recommend = localStorage.getItem('Recommend');

        if (
            (!Recommend || today !== Recommend) &&
            !isMobile &&
            callInfoReducer.calljson.isCompleted
        ) {
            // 推薦api
            (async function () {
                const urlParams = new URLSearchParams(window.location.search);
                const NormGroupID = urlParams.get('NormGroupID');
                const body = {
                    RecommendProductType: 'grp',
                    SearchCount: 8,
                    ProductID: NormGroupID,
                    ProductType: 'grp',
                    IsPriceGt: 0,
                };
                try {
                    const res = await fetch(`${CommonRoute}/json/recommendation`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(body),
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const bannerProduct: I_Banner = await res.json();
                    if (bannerProduct.Data) {
                        // 打亂原本資料傳入順序
                        if (bannerProduct.Data.length > 1) {
                            const adjustItem = bannerProduct.Data.shift();
                            bannerProduct.Data.push(adjustItem!);
                        }
                        setBanner(bannerProduct);
                    }
                } catch (ex) {
                    console.log('Fetch Banner Data Error', ex);
                }
            })();
        }
    }, [CommonRoute, callInfoReducer.calljson.isCompleted, isMobile]);

    // 3分鐘過後顯示逾時光箱
    useEffect(() => {
        // 逾時光箱狀態必須不等於暫不銷售及額滿
        if (isTimer && !recommends && Status !== 3 && Status !== 5) {
            setOpenLightBox(true);
        }
    }, [Status, isTimer, recommends]);

    // 顯示挪團/取消光箱
    useEffect(() => {
        setIsCancelled(Status === 7);
    }, [Status]);

    const hasBanner = banner.Data.length > 0;

    // 滑鼠移出時，雖然還沒拿到資料，但是等到資料傳入時，還是會開啟光箱
    useEffect(() => {
        setMouseLeave(false);
        if (mouseLeave && hasBanner && !openLightbox) {
            const date = new Date();
            const today = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
            setRecommends(true);
            // 將第一次打開推薦光箱寫進去cookie
            localStorage.setItem('Recommend', today);
            // 由於一開始頁面沒有渲染完成，所以推薦光箱一出現則新增此class避免滑動
            document.querySelector('body')?.classList.add('hidden');
        }
    }, [mouseLeave, hasBanner, openLightbox]);

    const isprod = process.env.ENV === 'prod';
    const isGroupfree = GroupInfo.TravelType === 2; // 判斷是否為團體自由行結果頁

    const secondLink = () => {
        const secondLink = {
            text: '',
            url: '',
        };

        if (isGroupfree) {
            return {
                text: '團體自由行',
                url: `//${isprod ? '' : 'u'}vacation.liontravel.com/category/zh-tw/index`,
            };
        }

        if (GroupInfo.IsForeign) {
            if (GroupInfo.ThemeID.length > 0) {
                secondLink.text = '主題旅遊';
                secondLink.url = '/tourpackage/theme/';
            } else {
                secondLink.text = '國外團體';
                secondLink.url = '/tourpackage/';
            }
        } else {
            if (GroupInfo.ThemeID.length > 0) {
                secondLink.text = '主題旅遊';
                secondLink.url = '/tourpackage/taiwan-theme/';
            } else {
                secondLink.text = '台灣團體';
                secondLink.url = '/tourpackage/taiwan/';
            }
        }

        return secondLink;
    };

    const homeDomain = (() => {
        switch (process.env.ENV) {
            case 'prod':
                return '//www.liontravel.com';
            default:
                return '//uwww.liontravel.com';
        }
    })();

    const routes = GroupInfo.GroupID
        ? [
              {
                  href: homeDomain,
                  text: '雄獅首頁',
              },
              {
                  href: isGroupfree ? secondLink().url : `${homeDomain}${secondLink().url}`,
                  text: secondLink().text,
              },
              {
                  href: undefined,
                  text: GroupInfo.TourName,
              },
          ]
        : [
              {
                  href: homeDomain,
                  text: '雄獅首頁',
              },
              {
                  href: `${homeDomain}/category/taiwan/`,
                  text: '台灣團體',
              },
              {
                  href: `${homeDomain}/category/`,
                  text: '國外團體',
                  spliter: '/',
              },
          ];

    //調整網址列參數
    useEffect(() => {
        changeDetailHistory({ NormGroupID: GroupInfo.NormGroupID, GroupID: GroupInfo.GroupID });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CalendarGroups.parmIsEnsureGroup]);

    const isSmallM = useMediaQuery({ query: '(max-width: 476px)' });
    const themeImg = React.useCallback(() => {
        const { ThemeID } = GroupInfo;

        // 沒有 ThemeID，則為一般團
        if (ThemeID.length <= 0) {
            return 'group';
        }

        // 若有 ThemeID，則以取第 1 項
        if (Object.values(themeTypes).includes(ThemeID[0] as themeTypes)) {
            return ThemeID[0];
        }

        // 若無對應，則回傳主題預設圖
        return '0';
    }, [GroupInfo]);

    const calculateDiscount = (orig: string, sales: string) => {
        if (!orig) {
            return 0;
        }
        const origNum = parseInt(orig.replace(/,/g, ''));
        const salesNum = parseInt(sales.replace(/,/g, ''));
        return ((origNum - salesNum) / origNum) * 100;
    };

    // 寫入ga4
    useEffect(() => {
        const nowGroup = groupReducer.GroupInfo;
        if (!GroupInfo.GroupID && !GroupInfo.NormGroupID && !nullGroup) {
            setNullGroup(true);
            return;
        }

        if (ga4GroupID === nowGroup.GroupID) return; // 用來避免 ga4 連續推同一個 groupID
        ga4GroupID = nowGroup.GroupID;

        let getBreadCrumb = '';
        routes.forEach((route) => {
            getBreadCrumb += route.text;
        });

        // 定義ga4Data
        const ga4Data = [
            {
                item_id: nowGroup.GroupID,
                item_list_id: nowGroup.NormGroupID?.toUpperCase(),
                item_name: nowGroup.TourName,
                affiliation: nowGroup.TourBuName === '旅天下' ? 'Uplan Travel' : 'Lion Travel',
                currency: 'TWD',
                discount: nowGroup.IsVip
                    ? calculateDiscount(nowGroup.OrigPrice, nowGroup.Price).toFixed(2)
                    : 0,
                item_brand: 'Lion Travel',
                item_category: getBreadCrumb.includes('台灣') ? 'B2CWebtwpd' : 'B2CWebpd',
                price: Number(`${nowGroup.Price?.split(',').join('')}`),
            },
        ];

        window.dataLayer.push({
            event: 'view_item',
            ecommerce: {
                items: nowGroup.Price !== null ? ga4Data : '',
            },
        });
    }, [GroupInfo.GroupID, GroupInfo.NormGroupID, groupReducer.GroupInfo, nullGroup, routes]);

    // 下架團 GA4推送
    useEffect(() => {
        if (nullGroup) {
            const ga4Data = [
                {
                    item_id: '',
                    item_list_id: '',
                    item_name: '',
                    affiliation: '',
                    currency: '',
                    discount: '',
                    item_brand: 'Lion Travel',
                    item_category: 'B2CWebpd',
                    price: 0,
                },
            ];
            window.dataLayer.push({
                event: 'view_item',
                ecommerce: {
                    items: ga4Data,
                },
            });
        }
    }, [nullGroup]);

    // 當推薦光箱關閉時，將避免滑動的css拿掉
    useEffect(() => {
        !recommends && document.querySelector('body')?.classList.remove('hidden');
    }, [recommends]);

    return (
        <React.Suspense fallback={null}>
            <SearchPanelBlockM isMobile={isMobile} />

            <div className={cN('detail', `theme_${themeImg()}`)}>
                {/* LCP 優化：將 CSS 背景圖改為 <img> 標籤並設定高優先級 */}
                {themeImg() === 'group' && (
                    <img
                        src={groupThemeImg}
                        alt=""
                        className="lcp-background-image"
                        aria-hidden="true"
                    />
                )}
                <SearchPanelBlock
                    pageType="detail"
                    routes={routes}
                    travelType={GroupInfo.TravelType.toString()}
                />
                {GroupInfo.GroupID ? (
                    <>
                        <GroupIntro />
                        <Itinerary />

                        {/* 世博期間暫時做法(不露出無關產品) */}
                        {!/世博|世界博覽會/.test(GroupInfo.TourName) && (
                            <Recommends isNoData={false} />
                        )}

                        <PrintLightbox isOpen={isPrintOpen} togglePrintLbx={togglePrintLbx} />
                    </>
                ) : (
                    <Recommends isNoData={true} />
                )}
            </div>

            {
                // 推薦產品光箱   世博期間暫時做法(不露出無關產品)
                (!isMobile || !/世博|世界博覽會/.test(GroupInfo.TourName)) && (
                    <LightBox
                        title="離開前再看一眼！推薦給你更多的團體行程優惠！"
                        open={recommends}
                        ToggleLightBox={() => setRecommends(false)}
                        className={cN('detail', 'banner_cards')}
                    >
                        <div className={cN('container', { place: swap })}>
                            {banner.Data.length > 4 && (
                                <div className={cN('arrow')} onClick={() => setSwap(false)}>
                                    {' '}
                                </div>
                            )}
                            <div
                                className={cN('card_container', {
                                    overfour: banner.Data.length < 4,
                                })}
                            >
                                {banner.Data.map((card, ind) => {
                                    return (
                                        <a
                                            className={cN('card')}
                                            key={ind}
                                            href={`${card.ProductURL}`}
                                            rel="noreferrer noopener"
                                            target="_blank"
                                            style={{
                                                left: swap
                                                    ? `-${(banner.Data.length - 4) * 194}px`
                                                    : '0',
                                            }}
                                        >
                                            <img src={card.ImageURL} loading="lazy" />
                                            <div>
                                                <div className={cN('title')}>
                                                    {card.ProductName}
                                                </div>
                                                <div className={cN('prices')}>
                                                    <div
                                                        className={cN('dashprice')}
                                                        style={{
                                                            visibility:
                                                                card.DirectPrice > 0
                                                                    ? 'visible'
                                                                    : 'hidden',
                                                        }}
                                                    >
                                                        TWD{card.DirectPrice.toLocaleString()}
                                                    </div>
                                                    <div className={cN('priceList')}>
                                                        {card.TradePrice === 0 ? (
                                                            <span>查看價格</span>
                                                        ) : (
                                                            <>
                                                                {card.ProductCurr
                                                                    ? card.ProductCurr
                                                                    : 'TWD'}
                                                                <span className={cN('price')}>
                                                                    {card.TradePrice.toLocaleString()}
                                                                </span>
                                                                人/起
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>

                            {banner.Data.length > 4 && (
                                <div className={cN('arrow')} onClick={() => setSwap(true)}>
                                    {' '}
                                </div>
                            )}
                        </div>
                    </LightBox>
                )
            }

            {
                // 逾時光箱
                GroupInfo.GroupID && (
                    <LightBox
                        open={openLightbox}
                        ToggleLightBox={() => {
                            setOpenLightBox(false);
                        }}
                        className={cN('detail', 'abandoned')}
                    >
                        <div className={cN('abandoned', 'container')}>
                            <img src={abandonedImg} loading="lazy" />
                            <div>
                                {!IsEnsureGroup && Status === 1
                                    ? '別急著離開，多看一眼熱銷行程吧!'
                                    : '行程即將額滿，別錯過了!'}
                            </div>
                            <button onClick={() => setOpenLightBox(false)}>繼續訂購</button>
                        </div>
                    </LightBox>
                )
            }

            {
                // 挪團/取消光箱
                !isLoadingCalendar && (
                    <LightBox
                        open={isCancelled}
                        ToggleLightBox={() => {
                            setIsCancelled(false);
                        }}
                        clickOutside={false}
                        className={cN('detail', 'cancelled')}
                    >
                        <div className={cN('container', 'cancelled_content')}>
                            <GuideWarning fill="#f03742" />
                            <h4 className={cN('title')}>出發日期已取消</h4>
                            {hasMoreGroups ? (
                                <p className={cN('text')}>
                                    您可以查看其他可報名日期，或重新進行搜尋
                                </p>
                            ) : (
                                <p className={cN('text')}>建議您重新搜尋其他行程</p>
                            )}
                            <div className="button_group">
                                <button
                                    className={hasMoreGroups ? 'white_btn' : 'red_btn'}
                                    onClick={() => (location.href = checkNewItinerary)}
                                >
                                    重新搜尋行程
                                </button>
                                {hasMoreGroups && (
                                    <button
                                        className="red_btn"
                                        onClick={() => {
                                            setIsCancelled(false);
                                            dispatch(setFindNearGroup(true));
                                        }}
                                    >
                                        查看可報名日期
                                    </button>
                                )}
                            </div>
                        </div>
                    </LightBox>
                )
            }
        </React.Suspense>
    );
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <Detail />
        </Provider>
    </React.StrictMode>
);

export default Detail;
